import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CircleNotch, ArrowLeft, EnvelopeSimple } from 'phosphor-react';

// Vertical logo (brain + GENI)
const LOGO_VERTICAL = "https://customer-assets.emergentagent.com/job_chart-analyzer-107/artifacts/zc7oaxip_brain%20%2B%20GENICERTO.png";

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent double submission (React StrictMode can cause double invocation)
    if (loading) return;
    
    setError('');
    setLoading(true);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth/reset-password`,
      });

      if (error) {
        setError(error.message || 'Failed to send reset email. Please try again.');
        setLoading(false);
        return;
      }

      setSuccess(true);
    } catch (err) {
      console.error('Reset password error:', err);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-4">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="flex flex-col items-center justify-center mb-8">
            <img
              src={LOGO_VERTICAL}
              alt="GENI"
              className="h-20 md:h-24 lg:h-28 w-auto object-contain mx-auto"
            />
          </div>

          {/* Success Message */}
          <div className="bg-[#121212] border border-white/10 rounded-lg p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-[#00E676]/10 flex items-center justify-center mx-auto mb-4">
              <EnvelopeSimple size={32} weight="duotone" className="text-[#00E676]" />
            </div>
            <h2 className="text-xl font-bold text-white mb-2">Check your email</h2>
            <p className="text-white/60 text-sm mb-6">
              We've sent a password reset link to <span className="text-white">{email}</span>
            </p>
            <p className="text-white/40 text-xs mb-6">
              Click the link in the email to reset your password. If you don't see it, check your spam folder.
            </p>
            <Link 
              to="/login" 
              className="text-[#00E676] hover:underline text-sm"
            >
              Back to login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex flex-col items-center justify-center mb-8">
          <img
            src={LOGO_VERTICAL}
            alt="GENI"
            className="h-20 md:h-24 lg:h-28 w-auto object-contain mx-auto"
          />
        </div>

        {/* Form Container */}
        <div className="bg-[#121212] border border-white/10 rounded-lg p-8">
          {/* Back Link */}
          <Link 
            to="/login" 
            className="flex items-center gap-2 text-white/60 hover:text-white transition-colors mb-6 group"
          >
            <ArrowLeft size={16} weight="bold" className="group-hover:-translate-x-1 transition-transform" />
            <span className="text-sm">Back to login</span>
          </Link>

          <h1 className="text-2xl font-bold text-white mb-2">Reset your password</h1>
          <p className="text-white/60 text-sm mb-6">
            Enter your email address and we'll send you a link to reset your password.
          </p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="email" className="text-white/80 mb-2 block">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                disabled={loading}
                className="bg-[#0A0A0A] border-white/10 text-white"
                data-testid="forgot-password-email"
              />
            </div>

            {error && (
              <div className="bg-[#FF3B30]/10 border border-[#FF3B30]/30 rounded-sm p-3">
                <p className="text-[#FF3B30] text-sm">{error}</p>
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-white text-black font-bold hover:bg-gray-200 transition-colors rounded-sm disabled:opacity-50"
              data-testid="forgot-password-submit"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <CircleNotch size={18} className="animate-spin" />
                  Sending...
                </span>
              ) : (
                'Send reset link'
              )}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
